import { getCountries } from '@/lib/data-source';
import { getOriginVacationWindows } from '@/lib/cross-calendar';
import { Flag } from '@/components/ui/Flag';
import { uiFb, getTemplateStr } from '@/lib/i18n';
import type { CountryCode } from '@/lib/types';

interface VacationWindowsListProps {
  destCountryCode: CountryCode;
  month: number;
  monthName: string;
  lang: string;
}

export function VacationWindowsList({ destCountryCode, month, monthName, lang }: VacationWindowsListProps) {
  return (
    <div className="mb-8 border-t border-border-subtle pt-6">
      <details>
        <summary className="cursor-pointer">
          <h2 className="inline font-heading text-xl font-bold text-text-primary md:text-2xl">
            {getTemplateStr('seo.cm.holiday_windows_heading', lang, {})}
          </h2>
          <span className="ml-2 text-xs text-text-secondary">{uiFb('ui.click_to_expand', '(click to expand)', lang)}</span>
        </summary>
        <p className="mt-2 text-sm text-text-secondary">
          {getTemplateStr('seo.cm.holiday_windows_desc', lang, { month: monthName })}
        </p>
        <div className="mt-4 space-y-1">
          {getCountries()
            .filter((c: { code: CountryCode }) => c.code !== destCountryCode)
            .map((c: { code: CountryCode; name: string; flag: string }) => {
              const vw = getOriginVacationWindows(c.code, month);
              if (vw.totalDaysOff === 0 && vw.holidays.length === 0) return null;
              return (
                <div
                  key={c.code}
                  className="flex items-center gap-3 content-card px-3 py-2"
                >
                  <Flag code={c.code} size={20} />
                  <span className="flex-1 text-sm font-medium text-text-primary">{c.name}</span>
                  <span className="rounded-full bg-surface-root px-2 py-0.5 text-xs text-text-secondary">
                    {getTemplateStr('seo.cm.n_holidays', lang, { count: String(vw.holidays.length) })}
                  </span>
                  <span className="rounded-full bg-surface-root px-2 py-0.5 text-xs text-text-secondary">
                    {getTemplateStr('seo.cm.n_long_weekends', lang, { count: String(vw.longWeekends.length) })}
                  </span>
                  {vw.totalDaysOff > 0 && (
                    <span className="badge-success rounded-full px-2 py-0.5 text-xs font-medium">
                      {getTemplateStr('seo.cross.days_off', lang, { count: String(vw.totalDaysOff) })}
                    </span>
                  )}
                </div>
              );
            })
            .filter(Boolean)}
        </div>
      </details>
    </div>
  );
}
